type AnalyticsParameter = boolean | number | string | undefined;

type AnalyticsParameters = Record<string, AnalyticsParameter>;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: {
			(command: "js", timestamp: Date): void;
			(command: "config", destinationId: string): void;
			(
				command: "event",
				eventName: string,
				parameters?: AnalyticsParameters,
			): void;
		};
	}
}

const GA4_MEASUREMENT_ID = "G-P2S6LKFT32";

/**
 * Every conversion value is reported in USD.
 *
 * Stripe settles in the customer's presentment currency, so its totals are not
 * comparable to one another and cannot carry a fixed currency label. Reporting
 * the published USD price instead keeps a plan worth the same amount to Ads
 * regardless of where the customer bought it.
 */
export const REPORTING_CURRENCY = "USD";

/** Configured for remarketing. Conversions reach Ads by importing GA4 key
 * events, so adding an Ads conversion action for an imported event would make
 * Ads count that conversion twice. */
const GOOGLE_ADS_ID = "AW-17835717198";

/**
 * Load and configure the Google tag.
 *
 * Only production reports. Staging and local development share the same
 * property, so gating here keeps test traffic out of the numbers that Ads bids
 * against. Nothing else in this module reports until this has run.
 */
export function initGoogleAnalytics(): void {
	if (import.meta.env.VITE_ENV !== "production") return;
	if (window.gtag) return;

	window.dataLayer = window.dataLayer ?? [];
	window.gtag = function gtag() {
		// The Google tag reads the Arguments object itself, so this must forward
		// it rather than spreading into an array.
		// biome-ignore lint/complexity/noArguments: required by the Google tag
		window.dataLayer?.push(arguments);
	};

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
	document.head.appendChild(script);

	window.gtag("js", new Date());
	window.gtag("config", GA4_MEASUREMENT_ID);
	window.gtag("config", GOOGLE_ADS_ID);
}

function canReportToGoogleAnalytics(): boolean {
	return (
		import.meta.env.VITE_ENV === "production" &&
		typeof window.gtag === "function"
	);
}

/**
 * Report a product action to GA4 without exposing personally identifiable data.
 *
 * The Google tag may be blocked, still loading, or replaced by a browser
 * extension, so analytics must always be a best-effort side effect and never
 * interrupt the user-facing product flow. Sign-up and checkout confirmation
 * both treat a thrown error as a failed operation, so a broken tag must never
 * escape the call it was only meant to observe.
 */
export function trackGoogleAnalyticsEvent(
	eventName: string,
	parameters?: AnalyticsParameters,
): void {
	if (!canReportToGoogleAnalytics()) return;

	try {
		window.gtag?.("event", eventName, parameters);
	} catch {
		// Reporting is best effort and the caller has no way to act on a failure.
	}
}

/**
 * Report an event at most once for the current browser session.
 *
 * Checkout confirmation is safely refreshable. GA4 does not deduplicate custom
 * events from a transaction identifier, so this guard prevents a refresh from
 * counting the same completed checkout again.
 *
 * The guard is only recorded once reporting is known to be possible. A disabled
 * or missing tag must not consume the single chance to report the event, or a
 * conversion would be dropped for the rest of the browser session with nothing
 * to signal the loss.
 */
export function trackGoogleAnalyticsEventOnce(
	eventName: string,
	id: string,
	parameters?: AnalyticsParameters,
): void {
	if (!canReportToGoogleAnalytics()) return;

	const storageKey = `coffitivity:ga4:${eventName}:${id}`;

	try {
		if (window.sessionStorage.getItem(storageKey)) return;
		window.sessionStorage.setItem(storageKey, "1");
	} catch {
		// Storage can be unavailable in private browsing. Reporting remains safe.
	}

	trackGoogleAnalyticsEvent(eventName, parameters);
}
